import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Zap, Star } from 'lucide-react';
import LiveDate from '../components/LiveDate';
import './Services.css';

const ALL_SERVICES = [
  {
    name: 'GEO', full: 'Generative Engine Optimization', icon: '⚡', path: '/services/geo',
    color: '#00d4ff', category: 'Top Pick',
    desc: 'Get cited in AI-generated responses from ChatGPT, Google Gemini, and Perplexity AI when users ask questions in your niche.',
    features: ['ChatGPT citation optimization', 'Google SGE visibility', 'Generative answer targeting', 'LLM content structuring'],
  },
  {
    name: 'AEO', full: 'Answer Engine Optimization', icon: '🎯', path: '/services/aeo',
    color: '#8b5cf6', category: 'Most Popular',
    desc: 'Appear in featured snippets, Google AI Overviews, and direct AI answers across all major search and assistant platforms.',
    features: ['Featured snippet domination', 'Voice search optimization', 'Position Zero targeting', 'FAQ schema implementation'],
  },
  {
    name: 'AIO', full: 'AI Optimization', icon: '🤖', path: '/services/aio',
    color: '#ec4899',
    desc: 'Full-spectrum AI presence optimization — ensuring your brand appears across every AI-powered platform and touchpoint.',
    features: ['Multi-platform AI strategy', 'Brand entity building', 'AI content auditing', 'Semantic SEO integration'],
  },
  {
    name: 'CAO', full: 'Conversational AI Optimization', icon: '💬', path: '/services/cao',
    color: '#f59e0b',
    desc: 'Optimize for conversational queries on voice assistants (Alexa, Siri, Google Assistant) and chat-based AI interfaces.',
    features: ['Voice query optimization', 'Natural language targeting', 'Intent mapping', 'Dialogue flow content'],
  },
  {
    name: 'LLMO', full: 'Large Language Model Optimization', icon: '🧠', path: '/services/llmo',
    color: '#10b981',
    desc: 'Optimize your content to be included in LLM training data and cited as an authoritative reference by GPT-4, Gemini, Claude.',
    features: ['Training data inclusion', 'Authority signal building', 'Citation optimization', 'Expert content creation'],
  },
  {
    name: 'GAIO', full: 'Generative AI Optimization', icon: '✨', path: '/services/gaio',
    color: '#00d4ff',
    desc: 'Ensure your brand and content is referenced when AI systems generate text, summaries, and recommendations.',
    features: ['Brand mention seeding', 'Topical authority building', 'Content depth expansion', 'AI-friendly formatting'],
  },
  {
    name: 'AISO', full: 'AI Search Optimization', icon: '🔍', path: '/services/aiso',
    color: '#8b5cf6', category: 'Future-Proof',
    desc: 'Dominate the new wave of AI-powered search engines that are replacing traditional search — including Perplexity and SearchGPT.',
    features: ['Perplexity AI optimization', 'SearchGPT readiness', 'You.com visibility', 'AI search citation strategy'],
  },
  {
    name: 'SRO', full: 'Search Response Optimization', icon: '📡', path: '/services/sro',
    color: '#ec4899',
    desc: 'Craft content specifically designed to be quoted verbatim in AI-generated search responses and answer summaries.',
    features: ['Quotable content creation', 'Response snippet targeting', 'Direct answer formatting', 'Zero-click strategy'],
  },
  {
    name: 'QEO', full: 'Query Engine Optimization', icon: '❓', path: '/services/qeo',
    color: '#f59e0b',
    desc: 'Match your content precisely to user query intent and AI interpretation to maximize relevance scoring.',
    features: ['Query intent mapping', 'Semantic content alignment', 'Question cluster targeting', 'Topic depth coverage'],
  },
  {
    name: 'GSO', full: 'Generative Search Optimization', icon: '🌐', path: '/services/gso',
    color: '#10b981',
    desc: 'Appear in Google SGE (Search Generative Experience) and Bing Copilot\'s AI-generated search results.',
    features: ['Google SGE targeting', 'Bing Copilot visibility', 'AI overview inclusion', 'SGE snippet optimization'],
  },
  {
    name: 'RAO', full: 'Response Algorithm Optimization', icon: '⚙️', path: '/services/rao',
    color: '#00d4ff',
    desc: 'Understand and adapt to how AI response algorithms rank and select sources for inclusion in generated answers.',
    features: ['Algorithm analysis', 'Ranking factor optimization', 'Response quality scoring', 'Technical AI SEO'],
  },
  {
    name: 'AAO', full: 'AI Answer Optimization', icon: '💡', path: '/services/aao',
    color: '#8b5cf6',
    desc: 'Optimize every touchpoint to maximize your brand\'s inclusion in AI-generated answers across all major platforms.',
    features: ['Cross-platform answer strategy', 'Answer accuracy signals', 'Brand knowledge graph', 'Entity optimization'],
  },
];

const PACKAGES = [
  {
    name: 'Starter',
    price: '₹15,000',
    period: '/month',
    color: 'var(--accent-cyan)',
    features: [
      'AEO + GEO optimization',
      'FAQ schema implementation',
      'Google AI Overview targeting',
      '1 AI platform monitoring',
      'Monthly report',
      '3 content pieces optimized',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Growth',
    price: '₹35,000',
    period: '/month',
    color: 'var(--accent-purple)',
    popular: true,
    features: [
      'All Starter features',
      'AEO + GEO + AISO + LLMO',
      '3 AI platforms monitoring',
      'ChatGPT & Gemini optimization',
      'Structured data overhaul',
      '8 content pieces/month',
      'Competitor AI gap analysis',
      'Bi-weekly strategy calls',
    ],
    cta: 'Most Popular',
  },
  {
    name: 'Dominate',
    price: '₹75,000',
    period: '/month',
    color: 'var(--accent-green)',
    features: [
      'All Growth features',
      'All 12 AEO services',
      'All AI platforms',
      'E-E-A-T authority building',
      'LLM training data targeting',
      '20+ content pieces/month',
      'Weekly strategy sessions',
      'Dedicated AI visibility dashboard',
      'Priority WhatsApp support',
    ],
    cta: 'Maximum Impact',
  },
];

const Services = () => {
  return (
    <>
      <Helmet>
        <title>AI Optimization Services Kerala | AEO, GEO, LLMO, AISO | Rajesh R</title>
        <meta name="description" content="Complete AI platform optimization services: AEO, GEO, LLMO, GAIO, AISO, SRO, CAO and more. Get found in ChatGPT, Gemini, Perplexity across Kerala. Contact Rajesh R for a custom quote." />
      </Helmet>

      <div className="page-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="badge"><Zap size={12} /> AI Optimization Services</div>
            <h1 className="section-title" style={{ marginTop: '16px', fontSize: 'clamp(28px,4vw,48px)' }}>
              Complete AI Visibility <span className="gradient-text">Service Suite</span>
            </h1>
            <p className="section-subtitle">
              Every AI optimization strategy you need to dominate ChatGPT, Gemini, Perplexity, and every AI platform that matters.
              Updated: <LiveDate className="live-date-inline" />
            </p>
          </motion.div>
        </div>
      </div>

      {/* ALL SERVICES */}
      <section className="all-services-section">
        <div className="container">
          <div className="all-services-grid">
            {ALL_SERVICES.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
              >
                <Link to={s.path} className="full-service-card">
                  {s.category && (
                    <div className="service-category-tag">
                      <Star size={10} /> {s.category}
                    </div>
                  )}
                  <div className="fsc-top">
                    <span className="service-icon">{s.icon}</span>
                    <span className="service-badge" style={{ color: s.color, background: `${s.color}18`, border: `1px solid ${s.color}30` }}>
                      {s.name}
                    </span>
                  </div>
                  <h3 style={{ fontSize: '17px', fontWeight: 600, marginBottom: '8px' }}>{s.full}</h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '16px' }}>{s.desc}</p>
                  <ul className="fsc-features">
                    {s.features.map(f => (
                      <li key={f}><Check size={12} color="var(--accent-green)" /> {f}</li>
                    ))}
                  </ul>
                  <div className="service-cta" style={{ marginTop: '16px' }}>
                    Learn More <ArrowRight size={14} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing-section">
        <div className="container">
          <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="badge">Transparent Pricing</div>
            <h2 className="section-title" style={{ marginTop: '16px' }}>
              Choose Your <span className="gradient-text">AI Visibility</span> Package
            </h2>
            <p className="section-subtitle">Flexible plans for businesses of all sizes across Kerala and beyond.</p>
          </motion.div>

          <div className="pricing-grid">
            {PACKAGES.map((pkg, i) => (
              <motion.div
                key={pkg.name}
                className={`pricing-card ${pkg.popular ? 'popular' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                {pkg.popular && <div className="popular-badge">⭐ Most Popular</div>}
                <h3 className="pkg-name">{pkg.name}</h3>
                <p className="pkg-tagline" style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
                  Custom pricing — <a href={`https://wa.me/917907038984?text=Hi%20Rajesh!%20I%E2%80%99m%20interested%20in%20the%20${encodeURIComponent(pkg.name)}%20AI%20optimization%20package.%20Can%20you%20share%20the%20pricing%20and%20details%3F`} target="_blank" rel="noreferrer" style={{ color: pkg.color }}>Contact for quote</a>
                </p>
                <ul className="pkg-features">
                  {pkg.features.map(f => (
                    <li key={f}><Check size={14} color={pkg.color} /> {f}</li>
                  ))}
                </ul>
                <a
                  href={`https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20checked%20your%20Services%20page%20and%20I%E2%80%99m%20interested%20in%20the%20${encodeURIComponent(pkg.name)}%20AI%20Optimization%20Package.%20Please%20share%20the%20pricing%20and%20next%20steps.`}
                  target="_blank" rel="noreferrer"
                  className={pkg.popular ? 'btn-primary' : 'btn-secondary'}
                  style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}
                >
                  {pkg.cta} <ArrowRight size={16} />
                </a>
              </motion.div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
              Custom enterprise packages available. <a href="https://wa.me/917907038984?text=Hi%20Rajesh!%20I%E2%80%99m%20looking%20for%20a%20custom%20enterprise%20AI%20Optimization%20package%20for%20my%20business.%20Can%20we%20discuss%20a%20tailored%20solution%3F" style={{ color: 'var(--accent-cyan)' }}>Contact Rajesh</a> for tailored quotes.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
